import React, { useState, useEffect, useCallback } from "react";
import { useNotification } from "@hooks/useNotifications";
import {
  makeMacroElement,
  processEditorTextToHtml,
  getMacrosList,
  areMacrosListsIdentical,
} from "features/editor/utils/utils";
import type { IMacro } from "features/editor/types";

export const useEditor = (
  contentEditableRef: React.RefObject<HTMLDivElement>,
  {
    index,
    translation,
    originalMacros,
    isOriginalOk,
    saveCursorPosition,
    restoreCursorPosition,
    hasCursorPosition,
    setCursorAfterElement,
    goTo,
  }: any,
) => {
  const initialText = translation.text;

  const { showError } = useNotification();
  const [macrosList, setMacrosList] = useState<string[]>(() => getMacrosList(initialText));
  const [editorHtml, setEditorHtml] = useState(() => processEditorTextToHtml(initialText, originalMacros));

  const shouldUpdateEditor = useCallback(() => {
    const currentText = contentEditableRef.current!.innerText;
    const currentMacrosList = getMacrosList(currentText);

    if (areMacrosListsIdentical(macrosList, currentMacrosList)) {
      return false;
    }

    return true;
  }, [macrosList, contentEditableRef]);

  useEffect(() => {
    restoreCursorPosition();
  }, [editorHtml]);

  // On editor change
  const onEditorChange = () => {
    saveCursorPosition();

    if (shouldUpdateEditor()) {
      const text = contentEditableRef.current?.innerText!;
      const html = processEditorTextToHtml(text, originalMacros);
      setMacrosList(() => getMacrosList(text));
      setEditorHtml(html);
    }
  };

  useEffect(() => {
    // Keyboard event handler
    function handleKeyDown(event: KeyboardEvent) {
      // Escape
      if (event.key === "Escape" || event.keyCode === 27) {
        event.preventDefault();

        contentEditableRef.current?.blur();
      }

      // Enter
      if (event.key === "Enter" || event.keyCode === 13) {
        event.preventDefault();

        contentEditableRef.current?.blur();
        goTo({ type: "next", direction: "next", currentIndex: index });
      }
    }

    // Paste behavior
    const handlePaste = (event: any) => {
      event.preventDefault();
      const { clipboardData } = event;
      const pastedData = clipboardData.getData("text");

      // Insert the pasted data at the caret position
      document.execCommand("insertText", false, pastedData);
    };

    contentEditableRef.current?.addEventListener("keydown", handleKeyDown);
    contentEditableRef.current?.addEventListener("paste", handlePaste);

    return () => {
      contentEditableRef.current?.removeEventListener("keydown", handleKeyDown);
      contentEditableRef.current?.removeEventListener("paste", handlePaste);
    };
  }, []);

  // Add element after cursor
  const addElementAfterCursor = (element: HTMLElement) => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      // Get the current range (the position of the cursor)
      const range = sel.getRangeAt(0);

      // Split the text node at the cursor position
      const textNode = range.startContainer;
      const offset = range.startOffset;

      if (textNode.nodeType === 3) {
        // if the node is a text node
        const text = textNode.nodeValue!;

        // Split the text node into two nodes
        const beforeText = text.slice(0, offset);
        const afterText = text.slice(offset);

        // Create a fragment with the macro
        const fragment = document.createDocumentFragment();
        fragment.appendChild(document.createTextNode(beforeText));
        fragment.appendChild(element);
        fragment.appendChild(document.createTextNode(afterText));

        const parentNode = textNode.parentNode!;
        parentNode.replaceChild(fragment, textNode);
      } else {
        // If it's not a text node, directly insert the new element
        range.insertNode(element);
      }
    }
  };

  // Macro click handler
  const handleMacroClick = (macro: IMacro) => {
    if (!isOriginalOk) {
      showError("Can't insert macro, original is not ok");
      return;
    }

    if (!hasCursorPosition()) {
      return;
    }

    restoreCursorPosition();

    const newElement = makeMacroElement(macro);

    addElementAfterCursor(newElement);
    setCursorAfterElement(newElement);

    onEditorChange();
  };

  return { editorHtml, onEditorChange, handleMacroClick };
};
